/**
 * ============================================================
 * KEJJA OS — src/lib/calendarApi.ts
 * ============================================================
 */

import { api } from '@/lib/api';

// ============================================================
// Types
// ============================================================

export type EventRecurrence = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom_weekdays';

export interface Calendar {
  id: string;
  homeId: string;
  ownerUserId: string | null;
  name: string;
  color: string;
  icon: string | null;
  isDefault: boolean;
  isVisible: boolean;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CalendarEvent {
  id: string;
  homeId: string;
  calendarId: string;
  title: string;
  description: string | null;
  location: string | null;
  startsAt: string;
  endsAt: string | null;
  allDay: boolean;
  recurrence: EventRecurrence;
  recurrenceDays: number[];
  recurrenceUntilDate: string | null;
  color: string | null;
  isReminder: boolean;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface EventOccurrence {
  id: string;
  occurrenceDate: string;
  startsAt: string;
  endsAt: string | null;
  allDay: boolean;
  title: string;
  description: string | null;
  location: string | null;
  color: string | null;
  isReminder: boolean;
  recurrence: EventRecurrence;
  calendarId: string;
  calendarName: string;
  calendarColor: string;
  calendarOwnerUserId: string | null;
  createdByUserId: string | null;
}

export interface RoutineOccurrence {
  id: string;
  occurrenceDate: string;
  title: string;
  icon: string | null;
  color: string | null;
  cadence: string;
  ownerUserId: string | null;
}

export interface CalendarPulse {
  today: string;
  upcoming: Array<{
    date: string;
    dayOfWeek: number;
    events: EventOccurrence[];
    routines: RoutineOccurrence[];
  }>;
  counts: {
    eventsNext7Days: number;
    remindersNext7Days: number;
    calendarsVisible: number;
  };
}

// ============================================================
// Payloads
// ============================================================

export interface CalendarCreatePayload {
  name: string;
  color?: string;
  icon?: string | null;
  ownerUserId?: string | null;
  isVisible?: boolean;
}

export type CalendarUpdatePayload = Partial<CalendarCreatePayload>;

export interface EventCreatePayload {
  calendarId: string;
  title: string;
  description?: string | null;
  location?: string | null;
  startsAt: string;
  endsAt?: string | null;
  allDay?: boolean;
  recurrence?: EventRecurrence;
  recurrenceDays?: number[];
  recurrenceUntilDate?: string | null;
  color?: string | null;
  isReminder?: boolean;
}

export type EventUpdatePayload = Partial<EventCreatePayload>;

// ============================================================
// API
// ============================================================

interface CalendarApi {
  pulse: (homeId: string) => Promise<CalendarPulse>;

  listCalendars: (homeId: string) => Promise<{ calendars: Calendar[] }>;
  createCalendar: (homeId: string, body: CalendarCreatePayload) => Promise<{ calendar: Calendar }>;
  updateCalendar: (homeId: string, calendarId: string, body: CalendarUpdatePayload) => Promise<{ calendar: Calendar }>;
  deleteCalendar: (homeId: string, calendarId: string) => Promise<void>;

  listEvents: (homeId: string, params: {
    from: string;
    to: string;
    calendarIds?: string[];
    includeRoutines?: boolean;
  }) => Promise<{ events: EventOccurrence[]; routines: RoutineOccurrence[] }>;
  getEvent: (homeId: string, eventId: string) => Promise<{ event: CalendarEvent }>;
  createEvent: (homeId: string, body: EventCreatePayload) => Promise<{ event: CalendarEvent }>;
  updateEvent: (homeId: string, eventId: string, body: EventUpdatePayload) => Promise<{ event: CalendarEvent }>;
  deleteEvent: (homeId: string, eventId: string) => Promise<void>;
}

const base = (homeId: string) => `/homes/${homeId}/calendar`;

export const calendarApi: CalendarApi = {
  pulse: (homeId) => api.get<CalendarPulse>(`${base(homeId)}/pulse`),

  listCalendars: (homeId) =>
    api.get<{ calendars: Calendar[] }>(`${base(homeId)}/calendars`),
  createCalendar: (homeId, body) =>
    api.post<{ calendar: Calendar }>(`${base(homeId)}/calendars`, body),
  updateCalendar: (homeId, calendarId, body) =>
    api.patch<{ calendar: Calendar }>(`${base(homeId)}/calendars/${calendarId}`, body),
  deleteCalendar: (homeId, calendarId) =>
    api.delete<void>(`${base(homeId)}/calendars/${calendarId}`),

  listEvents: (homeId, params) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    if (params.calendarIds?.length) qs.set('calendarIds', params.calendarIds.join(','));
    if (params.includeRoutines) qs.set('includeRoutines', 'true');
    return api.get<{ events: EventOccurrence[]; routines: RoutineOccurrence[] }>(
      `${base(homeId)}/events?${qs.toString()}`,
    );
  },
  getEvent: (homeId, eventId) =>
    api.get<{ event: CalendarEvent }>(`${base(homeId)}/events/${eventId}`),
  createEvent: (homeId, body) =>
    api.post<{ event: CalendarEvent }>(`${base(homeId)}/events`, body),
  updateEvent: (homeId, eventId, body) =>
    api.patch<{ event: CalendarEvent }>(`${base(homeId)}/events/${eventId}`, body),
  deleteEvent: (homeId, eventId) =>
    api.delete<void>(`${base(homeId)}/events/${eventId}`),
};
